import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, tap, switchMap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { AuthStore } from '@app/auth/auth.store';
import { JwtUtils } from '@app/@core/utils/jwt.utils';

export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token: string;
}

@Injectable({
  providedIn: 'root'
})
export class TokenService {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private http: HttpClient,
    private authStore: AuthStore
  ) {}

  refreshToken(): Observable<TokenResponse> {
    if (this.isRefreshing) {
      return this.refreshTokenSubject.asObservable();
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    const refreshToken = this.authStore.getValue().refresh_token;
    
    console.log('TokenService: Attempting to refresh token');
    console.log('TokenService: Refresh token exists:', !!refreshToken);
    console.log('TokenService: Refresh token length:', refreshToken?.length);
    
    if (!refreshToken) {
      this.isRefreshing = false;
      return throwError('No refresh token available');
    }

    const requestBody = { refresh_token: refreshToken };
    console.log('TokenService: Making refresh request to:', `${environment.authUrl}/refresh`);
    console.log('TokenService: Request body:', requestBody);

    return this.http.post<TokenResponse>(`${environment.authUrl}/refresh`, requestBody).pipe(
      tap((response: TokenResponse) => {
        console.log('TokenService: Refresh successful:', response);
        this.isRefreshing = false;
        
        // Update auth store with new tokens
        const currentAuth = this.authStore.getValue();
        this.authStore.save({
          ...currentAuth,
          access_token: response.access_token,
          refresh_token: response.refresh_token,
          expires_in: response.expires_in
        });
        
        this.refreshTokenSubject.next(response);
      }),
      catchError((error) => {
        console.error('TokenService: Refresh failed:', error);
        console.error('TokenService: Error status:', error.status);
        console.error('TokenService: Error message:', error.message);
        this.isRefreshing = false;
        this.refreshTokenSubject.next(null);
        
        // Clear auth state on refresh failure
        this.authStore.remove();
        
        return throwError(error);
      })
    );
  }

  isTokenExpired(): boolean {
    const auth = this.authStore.getValue();
    if (!auth.access_token) return true;
    
    return JwtUtils.isTokenExpired(auth.access_token);
  }

  shouldRefreshToken(): boolean {
    const auth = this.authStore.getValue();
    if (!auth.access_token) return true;
    
    return JwtUtils.shouldRefreshToken(auth.access_token, 5); // 5 minutes buffer
  }
}
